import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// --- Define the specific orderby type ---
export type OrderByOptions = "popularity" | "date" | "price-asc" | "price-desc";

// Define types for our state
export interface FilterState {
  category: number[];
  tag: number[];
  brand: string[];
  minPrice: number | null;
  maxPrice: number | null;
  search: string;
  orderby: OrderByOptions;
  onSale: boolean;
  sku: string;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  perPage: number;
}

export interface UIState {
  activeView: "grid" | "list";
}

export interface ProductState {
  filters: FilterState;
  pagination: PaginationState;
  ui: UIState;
}

// Define types for our action payloads
interface CategoryActionPayload {
  action?: "clear";
  replace?: number[];
}

interface PriceRangePayload {
  minPrice: number | null;
  maxPrice: number | null;
}

const initialState: ProductState = {
  filters: {
    category: [],
    tag: [],
    brand: [],
    minPrice: null,
    maxPrice: null,
    search: "",
    orderby: "date",
    onSale: false,
    sku: "",
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    perPage: 20,
  },
  ui: {
    activeView: "grid",
  },
};

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      state.pagination.currentPage = 1;
    },
    setCategory: (
      state,
      action: PayloadAction<number | CategoryActionPayload>
    ) => {
      state.filters.sku = "";
      if (
        typeof action.payload === "object" &&
        action.payload.action === "clear"
      ) {
        state.filters.category = [];
      } else if (typeof action.payload === "object" && action.payload.replace) {
        state.filters.category = action.payload.replace;
      } else {
        const category = action.payload as number;
        if (state.filters.category.includes(category)) {
          state.filters.category = state.filters.category.filter(
            (cat) => cat !== category
          );
        } else {
          state.filters.category = [...state.filters.category, category];
        }
      }
      state.pagination.currentPage = 1;
    },
    setTag: (state, action: PayloadAction<number>) => {
      state.filters.sku = "";
      const tag = action.payload;
      if (state.filters.tag.includes(tag)) {
        state.filters.tag = state.filters.tag.filter((t) => t !== tag);
      } else {
        state.filters.tag = [...state.filters.tag, tag];
      }
      state.pagination.currentPage = 1;
    },
    setBrand: (state, action: PayloadAction<string>) => {
      state.filters.sku = "";
      const brandSlug = action.payload;
      if (state.filters.brand.includes(brandSlug)) {
        state.filters.brand = state.filters.brand.filter(
          (slug) => slug !== brandSlug
        );
      } else {
        state.filters.brand = [...state.filters.brand, brandSlug];
      }
      state.pagination.currentPage = 1;
    },
    setPriceRange: (state, action: PayloadAction<PriceRangePayload>) => {
      state.filters.sku = "";
      const { minPrice, maxPrice } = action.payload;
      state.filters.minPrice = minPrice;
      state.filters.maxPrice = maxPrice;
      state.pagination.currentPage = 1;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      // If a search is performed, it should be exclusive.
      Object.assign(state.filters, initialState.filters, {
        search: action.payload,
      });
      state.pagination.currentPage = 1;
    },
    setSku: (state, action: PayloadAction<string>) => {
      // If an SKU search is performed, it should be exclusive.
      Object.assign(state.filters, initialState.filters, {
        sku: action.payload,
      });
      state.pagination.currentPage = 1;
    },
    setSorting: (state, action: PayloadAction<OrderByOptions>) => {
      state.filters.orderby = action.payload;
      state.pagination.currentPage = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.perPage = action.payload;
      state.pagination.currentPage = 1;
    },
    setActiveView: (state, action: PayloadAction<"grid" | "list">) => {
      state.ui.activeView = action.payload;
    },
    setOnSale: (state, action: PayloadAction<boolean>) => {
      state.filters.sku = "";
      state.filters.onSale = action.payload;
      state.pagination.currentPage = 1;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
  },
});

export const {
  setFilters,
  setCategory,
  setTag,
  setBrand,
  setPriceRange,
  setSearch,
  setSku,
  setSorting,
  setPagination,
  setPage,
  setPerPage,
  setActiveView,
  setOnSale,
  resetFilters,
} = productSlice.actions;

export default productSlice.reducer;
